import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Radio } from 'antd';
import Swal from 'sweetalert2';
import { ClipLoader } from 'react-spinners';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import history from '~/services/history';

import * as actions from '~/store/modules/careLines/actions';

import {
  comorbidades,
  dadosDaDoenca,
  gravidadeClinica,
  conveniosPaciente,
  sobreInicioSintomas,
  dataHoraRevisao,
  dataHoraImpressao,
  dataHoraSintomas,
  dataHoraPrimeiraColeta,
  dataHoraSegundaColeta,
  dataHoraPrimeiraHemocultura,
  dataHoraAntimicrobiano,
  dataHoraSaida,
  reposicaoVolemicaAdequada,
  dataHoraReposicaoVolemica,
  covidSepse,
  vasopressores,
  infusaoVasopressor,
  medidaPressao,
  pressaoArterial,
  motivoSaida,
} from './options';

import Breadcumbs from '~/components/Breadcumbs';
import ErrorField from '~/components/ErrorField';

// eslint-disable-next-line import/extensions
import { LcSepseSchema } from '~/utils/schemas/lcSepseSchema';
import {
  numbersAndComa,
  onlyNumbers,
  scrollPageTo,
} from '~/helpers/generalFunctions';

import {
  PageTitle,
  BoxContent,
  Content,
  TitleSection,
  BoxInput,
  Label,
  BoxRadios,
  ButtonBox,
  BoxForm,
} from '../styles/style';

import DadoFaltantes from '../components/DadosFaltantes';
import { Input } from '~/components/Input';
import { RadioInput, RadioInputFlex } from '../components/RadioInput';

import { modalConfirm } from '../../LinhasDeCuidadoFormulario/dados/optionsModal';
import ButtonReset from '../components/ButtonReset';
import ButtonBack from '~/components/ButtonBack';
import { toast } from 'react-toastify';
import ExceptionsForm from '../components/ExceptionsForm';
import { getTitleIndicador } from '../careLinesInfo';
import { IoMdHelpCircle } from 'react-icons/io';

const FichadeColetaSepse = ({ location }) => {
  const dispath = useDispatch();

  const { id } = location?.state;

  const { careLineData, loading, errorData } = useSelector(
    state => state.careLines
  );

  const firstFocus = useRef(null);

  const [dados, setDados] = useState(null);
  const [numeroPaciente, setNumeroPaciente] = useState('');

  useEffect(() => {
    // limpa erros
    dispath(
      actions.sepseFailure({
        errorData: null,
      })
    );
  }, []);

  useEffect(() => {
    if (location?.state?.id) {
      dispath(
        actions.sepseGetRequest({
          id: location?.state?.id,
        })
      );
    }
  }, [location?.state]);

  useEffect(() => {
    if (id && careLineData) {
      setDados(careLineData);
    }
  }, [careLineData]);

  // Lógica de Ramificação:
  const [reposicaoVolemica, setReposicaoVolemica] = useState(false);
  const [vasop, setVasop] = useState(false);
  const [exibeInfusao, setExibeInfusao] = useState(false);

  // Reset Button
  const [isLoaded, setIsloaded] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setIsloaded(true);
    }, 2000);
  }, []);

  // End Reset Button

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      num_registro_sepse: dados?.num_registro_sepse?.toString() || null,
      sexo_sepse: dados?.sexo_sepse?.toString() || null,
      dn_sepse: dados?.dn_sepse?.toString() || null,
      convenio_sepse: dados?.convenio_sepse?.toString() || null,
      imuno_sepse: dados?.imuno_sepse?.toString() || null,
      neoplasia_sepse: dados?.neoplasia_sepse?.toString() || null,
      avc_sepse: dados?.avc_sepse?.toString() || null,
      cirrose_sepse: dados?.cirrose_sepse?.toString() || null,
      drc_sepse: dados?.drc_sepse?.toString() || null,
      dm_sepse: dados?.dm_sepse?.toString() || null,
      infeccao_sepse: dados?.infeccao_sepse?.toString() || null,
      apre_clinica_sepse: dados?.apre_clinica_sepse?.toString() || null,
      sintomas_data_sepse: dados?.sintomas_data_sepse?.toString() || null,
      sintomas_hora_sepse: dados?.sintomas_hora_sepse?.toString() || null,
      sintomas_local_sepse: dados?.sintomas_local_sepse?.toString() || null,
      diagnostico_data_sepse: dados?.diagnostico_data_sepse?.toString() || null,
      diagnostico_hora_sepse: dados?.diagnostico_hora_sepse?.toString() || null,
      pas_diagnostico_sepse: dados?.pas_diagnostico_sepse?.toString() || null,
      pad_diagnostico_sepse: dados?.pad_diagnostico_sepse?.toString() || null,
      lactato1_data_sepse: dados?.lactato1_data_sepse?.toString() || null,
      lactato1_hora_sepse: dados?.lactato1_hora_sepse?.toString() || null,
      lactato1_resultado_sepse:
        dados?.lactato1_resultado_sepse?.toString() || null,
      lactato1_unidade_sepse: dados?.lactato1_unidade_sepse?.toString() || null,
      lactato2_data_sepse: dados?.lactato2_data_sepse?.toString() || null,
      lactato2_hora_sepse: dados?.lactato2_hora_sepse?.toString() || null,
      lactato2_resultado_sepse:
        dados?.lactato2_resultado_sepse?.toString() || null,
      lactato2_unidade_sepse: dados?.lactato2_unidade_sepse?.toString() || null,
      hemocultura_data_sepse: dados?.hemocultura_data_sepse?.toString() || null,
      hemocultura_hora_sepse: dados?.hemocultura_hora_sepse?.toString() || null,
      antimicrobiano_previo_sepse:
        dados?.antimicrobiano_previo_sepse?.toString() || null,
      antimicrobiano_data_sepse:
        dados?.antimicrobiano_data_sepse?.toString() || null,
      antimicrobiano_hora_sepse:
        dados?.antimicrobiano_hora_sepse?.toString() || null,
      rep_vol_realizacao_sepse:
        dados?.rep_vol_realizacao_sepse?.toString() || null,
      rep_vol_data_sepse: dados?.rep_vol_data_sepse?.toString() || null,
      rep_vol_hora_sepse: dados?.rep_vol_hora_sepse?.toString() || null,
      pas_infusao_sepse: dados?.pas_infusao_sepse?.toString() || null,
      pad_infusao_sepse: dados?.pad_infusao_sepse?.toString() || null,
      pa_infusao_data_sepse: dados?.pa_infusao_data_sepse?.toString() || null,
      pa_infusao_hora_sepse: dados?.pa_infusao_hora_sepse?.toString() || null,
      vasop_realizacao_sepse: dados?.vasop_realizacao_sepse?.toString() || null,
      vasop_data_sepse: dados?.vasop_data_sepse?.toString() || null,
      vasop_hora_sepse: dados?.vasop_hora_sepse?.toString() || null,
      revisao_data_sepse: dados?.revisao_data_sepse?.toString() || null,
      revisao_hora_sepse: dados?.revisao_hora_sepse?.toString() || null,
      paliativos_sepse: dados?.paliativos_sepse?.toString() || null,
      covid_sepse: dados?.covid_sepse?.toString() || null,
      saida_motivo_sepse: dados?.saida_motivo_sepse?.toString() || null,
      saida_data_sepse: dados?.saida_data_sepse?.toString() || null,
      saida_hora_sepse: dados?.saida_hora_sepse?.toString() || null,
    },
    validationSchema: LcSepseSchema,
    onSubmit: async values => {
      if (id) {
        const payload = {
          ...dados,
          ...values,
        };

        return dispath(
          actions.sepseUpdateRequest({
            data: payload,
            id: id,
            periodo: location?.state?.periodo,
          })
        );
      }
      dispath(
        actions.sepseRequest({
          data: values,
          periodo: location?.state?.periodo,
        })
      );
    },
  });

  const callHandleSubmit = () => {
    Swal.fire(modalConfirm).then(result => {
      if (result.value) {
        formik.handleSubmit();
        // limpa erros
        dispath(
          actions.sepseFailure({
            errorData: null,
          })
        );
      }
    });
  };

  // setExibeInfusao
  useEffect(() => {
    if (
      formik?.values?.pad_infusao_sepse &&
      formik?.values?.pas_infusao_sepse
    ) {
      setExibeInfusao(true);
    } else {
      setExibeInfusao(false);
      formik.setFieldValue('pa_infusao_data_sepse', null);
      formik.setFieldValue('pa_infusao_hora_sepse', null);
    }
  }, [formik?.values?.pad_infusao_sepse, formik?.values?.pas_infusao_sepse]);

  useEffect(() => {
    if (dados?.rep_vol_realizacao_sepse?.toString() === '1') {
      setReposicaoVolemica(true);
    }
  }, [dados]);

  const exibeRamificacao = useCallback(
    (name, value) => {
      if (name == 'rep_vol_realizacao_sepse') {
        if (value == '1') {
          setReposicaoVolemica(true);
        } else {
          setReposicaoVolemica(false);
          formik.setFieldValue('rep_vol_data_sepse', null);
          formik.setFieldValue('rep_vol_hora_sepse', null);
          formik.setFieldValue('pas_infusao_sepse', null);
          formik.setFieldValue('pad_infusao_sepse', null);
          formik.setFieldValue('vasop_realizacao_sepse', null);

          // reset ramificacao 2
          formik.setFieldValue('vasop_data_sepse', null);
          formik.setFieldValue('vasop_hora_sepse', null);
          setVasop(false);
        }
        formik.setFieldValue('rep_vol_realizacao_sepse', value);
      }
    },
    [formik]
  );

  const exibeRamificacao2 = useCallback(
    (name, value) => {
      if (name == 'vasop_realizacao_sepse') {
        if (value == '1') {
          setVasop(true);
        } else {
          setVasop(false);
          formik.setFieldValue('vasop_data_sepse', null);
          formik.setFieldValue('vasop_hora_sepse', null);
        }
      }
      formik.setFieldValue(`${name}`, value);
    },
    [formik]
  );

  // Lógica de ramificação ao Editar o formulário vindo do Grid

  useEffect(() => {
    if (dados?.rep_vol_realizacao_sepse && dados?.vasop_realizacao_sepse) {
      exibeRamificacao(
        'rep_vol_realizacao_sepse',
        dados?.rep_vol_realizacao_sepse?.toString()
      );
      exibeRamificacao2(
        'vasop_realizacao_sepse',
        dados?.vasop_realizacao_sepse?.toString()
      );
    }
  }, [dados]);

  useEffect(() => {
    if (
      formik?.values?.rep_vol_realizacao_sepse &&
      formik?.values?.vasop_realizacao_sepse
    ) {
      exibeRamificacao(formik.values.rep_vol_realizacao_sepse);
      exibeRamificacao2(
        'vasop_realizacao_sepse',
        formik.values.vasop_realizacao_sepse
      );
    }
  }, [
    formik?.values?.rep_vol_realizacao_sepse,
    formik?.values?.vasop_realizacao_sepse,
  ]);

  const handleResetGroup = useCallback(group => {
    group.map(item => {
      formik.setFieldValue(`${item.name}`, null);
      exibeRamificacao(`${item.name}`, null);
      exibeRamificacao2(`${item.name}`, null);
    });
  }, []);

  //
  useEffect(() => {
    scrollPageTo(75);
  }, []);

  useEffect(() => {
    const keyErrors = Object.keys(formik.errors);
    if (keyErrors.length > 0 && formik.isSubmitting) {
      setTimeout(() => {
        const errorElement = document.querySelector(`[name="${keyErrors[0]}"]`);
        if (errorElement)
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    }
  }, [formik.submitCount, formik.errors]);

  const getErrosPdf = useCallback(() => {
    const { indicador } = location.state;
    history.push({
      pathname: `/area-de-suporte`,
      state: {
        searchBy: `${getTitleIndicador(indicador)}`,
      },
    });
  }, []);

  return (
    <>
      <Breadcumbs
        path="sepse_e_choque_septico"
        pathTitle="Adicionar Indicadores de Linhas de Cuidado / Sepse e Choque Séptico"
      />
      <PageTitle>
        <div>
          <ButtonBack />
          <span ref={firstFocus}>Ficha de coleta</span>
        </div>

        {errorData?.fields && isLoaded && (
          <button className="errorButton" type="button" onClick={getErrosPdf}>
            <IoMdHelpCircle size={16} color="#FFF" />
            Documentação de erros
          </button>
        )}
      </PageTitle>

      {loading ? (
        <BoxContent>
          <div className="boxSpin">
            <ClipLoader size={50} color="#74a50a" loading={loading} />
          </div>
        </BoxContent>
      ) : (
        <BoxContent>
          <div className="title">
            <h2>Preencha abaixo as informações:</h2>
          </div>

          <div className="BoxErros">
            {errorData?.fields && (
              <ExceptionsForm
                lc="sepse_e_choque_septico"
                exceptions={errorData?.fields}
              />
            )}
          </div>

          <Content>
            <form onSubmit={formik.handleSubmit} method="post">
              {/* Linha de Cuidado, Competência e Dados Gerais */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <BoxInput>
                      <Col span={12} className="item">
                        <TitleSection className="font-green">
                          {'>'} Linha de Cuidado
                        </TitleSection>
                        <input
                          type="text"
                          name="indicadorNome"
                          defaultValue={
                            (location.state && location.state.indicadorNome) ||
                            ''
                          }
                          disabled
                        />
                      </Col>
                      <Col span={12} className="item">
                        <TitleSection className="font-green">
                          {'>'} Competência
                        </TitleSection>
                        <input
                          type="text"
                          name="periodo"
                          defaultValue={
                            location.state && location.state.periodo
                          }
                          disabled
                        />
                      </Col>
                    </BoxInput>
                  </Col>
                </Row>

                <hr />
                <Row>
                  <Col span={24}>
                    <Row>
                      <Row>
                        <Col span={12}>
                          <TitleSection>
                            {'>'} Dados Gerais do paciente
                          </TitleSection>
                        </Col>
                      </Row>
                      <Col span={12} className="item">
                        <Input
                          name="num_registro_sepse"
                          label="1) Número do registro"
                          type="text"
                          maxLength={5}
                          // placeholder="Número do registro"
                          value={formik.values.num_registro_sepse}
                          hasError={
                            formik.errors.num_registro_sepse &&
                            formik.touched.num_registro_sepse
                              ? formik.errors.num_registro_sepse
                              : ''
                          }
                          onChange={({ target }) => {
                            formik.setFieldValue(
                              'num_registro_sepse',
                              onlyNumbers(target.value)
                            );
                          }}
                        />
                      </Col>
                      <Col span={12} className="item">
                        <Row>
                          <Col span={12}>
                            <Label>2{')'} Sexo</Label>
                          </Col>
                          <Col span={12}>
                            {isLoaded && (
                              <ButtonReset
                                resetGroup={[{ name: 'sexo_sepse' }]}
                                handleResetGroup={handleResetGroup}
                              />
                            )}
                          </Col>
                        </Row>
                        <BoxInput>
                          <div className="inlineRadios">
                            <BoxRadios
                              className="v-large"
                              active={formik.values.sexo_sepse === '1'}
                            >
                              <Radio
                                name="sexo_sepse"
                                value="1"
                                checked={formik.values.sexo_sepse === '1'}
                                onChange={() =>
                                  formik.setFieldValue('sexo_sepse', '1')
                                }
                              >
                                Feminino
                              </Radio>
                            </BoxRadios>
                            <BoxRadios
                              className="v-large"
                              active={formik.values.sexo_sepse === '0'}
                            >
                              <Radio
                                name="sexo_sepse"
                                value="0"
                                checked={formik.values.sexo_sepse === '0'}
                                onChange={() =>
                                  formik.setFieldValue('sexo_sepse', '0')
                                }
                              >
                                Masculino
                              </Radio>
                            </BoxRadios>
                          </div>
                        </BoxInput>

                        {formik.errors.sexo_sepse && formik.submitCount > 0 ? (
                          <ErrorField message={formik.errors.sexo_sepse} />
                        ) : null}
                      </Col>
                    </Row>
                  </Col>

                  <Col span={24}>
                    <Row>
                      <Col span={12} className="item">
                        <DadoFaltantes
                          title="3) Data de nascimento"
                          name="dn_sepse"
                          formik={formik}
                          errors={formik.errors}
                          hasError={
                            (formik.errors.dn_sepse &&
                              formik.touched.dn_sepse) ||
                            formik.submitCount > 0
                              ? formik.errors.dn_sepse
                              : ''
                          }
                        />
                      </Col>

                      <Col span={12} className="item">
                        {conveniosPaciente.map(item => (
                          <RadioInputFlex
                            key={`${item.id}-${item.name}`}
                            item={item}
                            formik={formik}
                          />
                        ))}
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </BoxForm>
              <hr />

              {/* Comorbidades */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <Col span={12}>
                        <h4>5{')'} Comorbidades</h4>
                      </Col>
                      <Col span={12}>
                        {isLoaded && (
                          <ButtonReset
                            resetGroup={comorbidades}
                            handleResetGroup={handleResetGroup}
                          />
                        )}
                      </Col>
                    </Row>

                    {comorbidades.map(item => (
                      <RadioInputFlex
                        key={`${item.id}-${item.name}`}
                        item={item}
                        noButton
                        formik={formik}
                      />
                    ))}
                  </Col>
                </Row>
              </BoxForm>

              <hr />

              {/* Dados da doença */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <Col span={12}>
                        <TitleSection>{'>'} Dados da doença</TitleSection>
                      </Col>
                    </Row>
                    {dadosDaDoenca.map(item => (
                      <Row key={item.id}>
                        <Row>
                          <Col span={12}>
                            <Label>{item.label}</Label>
                          </Col>
                          <Col span={12}>
                            {isLoaded && (
                              <ButtonReset
                                resetGroup={[...dadosDaDoenca]}
                                handleResetGroup={handleResetGroup}
                              />
                            )}
                          </Col>
                        </Row>
                        <BoxInput>
                          {item.opcoes.map(opcao => (
                            <Col key={opcao.id}>
                              <BoxRadios
                                className="v-large"
                                active={
                                  formik.values[item.name] === opcao.value
                                }
                                style={{ maxWidth: '100%' }}
                              >
                                <Radio
                                  name={item.name}
                                  value={formik.values[opcao.value]}
                                  checked={
                                    formik.values[item.name] === opcao.value
                                  }
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `${item.name}`,
                                      opcao.value
                                    )
                                  }
                                >
                                  {opcao.descricao}
                                </Radio>
                              </BoxRadios>
                            </Col>
                          ))}
                        </BoxInput>

                        {formik.errors[item.name] && formik.submitCount > 0 ? (
                          <ErrorField message={formik.errors[item.name]} />
                        ) : null}
                      </Row>
                    ))}
                    {gravidadeClinica.map(item => (
                      <Row key={item.id}>
                        <Row>
                          <Col span={12}>
                            <Label>{item.label}</Label>
                          </Col>
                          <Col span={12}>
                            {isLoaded && (
                              <ButtonReset
                                resetGroup={[...gravidadeClinica]}
                                handleResetGroup={handleResetGroup}
                              />
                            )}
                          </Col>
                        </Row>
                        <BoxInput>
                          {item.opcoes.map(opcao => (
                            <Col key={opcao.id}>
                              <BoxRadios
                                className="v-large"
                                active={
                                  formik.values[item.name] === opcao.value
                                }
                                style={{ maxWidth: '100%' }}
                              >
                                <Radio
                                  name={item.name}
                                  value={formik.values[opcao.value]}
                                  checked={
                                    formik.values[item.name] === opcao.value
                                  }
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `${item.name}`,
                                      opcao.value
                                    )
                                  }
                                >
                                  {opcao.descricao}
                                </Radio>
                              </BoxRadios>
                            </Col>
                          ))}
                        </BoxInput>

                        {formik.errors[item.name] && formik.submitCount > 0 ? (
                          <ErrorField message={formik.errors[item.name]} />
                        ) : null}
                      </Row>
                    ))}
                  </Col>
                </Row>
              </BoxForm>
              <hr />

              {/* Data e hora do início dos sintomas de disfunção orgânica */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <h4>
                        8{')'} Data e hora do início dos sintomas de disfunção
                        orgânica
                      </h4>
                    </Row>
                    <Row>
                      {dataHoraSintomas.map(item => (
                        <Col span={12} className="item" key={item.id}>
                          <DadoFaltantes
                            title={item.label}
                            name={item.name}
                            formik={formik}
                            errors={formik.errors}
                            hasError={
                              (formik.errors[item.name] &&
                                formik.touched[item.name]) ||
                              formik.submitCount > 0
                                ? formik.errors[item.name]
                                : ''
                            }
                          />
                        </Col>
                      ))}
                    </Row>
                  </Col>
                </Row>
              </BoxForm>
              <hr />

              {/* Sobre a data e hora do início dos sintomas de disfunção orgânica */}
              <BoxForm>
                {sobreInicioSintomas.map(item => (
                  <BoxInput key={item.id}>
                    <Row>
                      <Row>
                        <Col span={16}>
                          <Label>{item.label}</Label>
                        </Col>
                        <Col span={8}>
                          {isLoaded && (
                            <ButtonReset
                              resetGroup={[...sobreInicioSintomas]}
                              handleResetGroup={handleResetGroup}
                            />
                          )}
                        </Col>
                      </Row>
                      <div className="inlineRadiosBox">
                        {item.opcoes.map(opcao => (
                          <Col span={12} key={opcao.id}>
                            <BoxRadios
                              className="v-large"
                              active={formik.values[item.name] === opcao.value}
                              style={{ maxWidth: '100%', minHeight: 78 }}
                            >
                              <Radio
                                name={item.name}
                                value={formik.values[opcao.value]}
                                checked={
                                  formik.values[item.name] === opcao.value
                                }
                                onChange={() =>
                                  formik.setFieldValue(
                                    `${item.name}`,
                                    opcao.value
                                  )
                                }
                              >
                                {opcao.descricao}
                              </Radio>
                            </BoxRadios>
                            {formik.errors[item.name] &&
                            formik.submitCount > 0 ? (
                              <ErrorField message={formik.errors[item.name]} />
                            ) : null}
                          </Col>
                        ))}
                      </div>
                    </Row>
                  </BoxInput>
                ))}
              </BoxForm>
              <hr />

              {/* Data e hora da impressão diagnóstica de sepse ou choque séptico */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <h4>
                        10{')'} Data e hora da impressão diagnóstica de sepse ou
                        choque séptico
                      </h4>
                    </Row>
                    <Row>
                      {dataHoraImpressao.map(item => (
                        <Col span={12} className="item" key={item.id}>
                          <DadoFaltantes
                            title={item.label}
                            name={item.name}
                            formik={formik}
                            errors={formik.errors}
                            hasError={
                              (formik.errors.dn_sepse &&
                                formik.touched.dn_sepse) ||
                              formik.submitCount > 0
                                ? formik.errors.dn_sepse
                                : ''
                            }
                          />
                        </Col>
                      ))}
                    </Row>
                  </Col>
                </Row>
              </BoxForm>
              <hr />

              {/* Exames */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <TitleSection>{'>'} Exames</TitleSection>
                    </Row>
                    <Row>
                      <h4>
                        11{')'} Pressão arterial sistólica logo após a suspeita
                        de sepse ou choque séptico
                      </h4>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <BoxInput>
                          <Col span={12} className="item">
                            <Input
                              name="pas_diagnostico_sepse"
                              label="Pressão arterial sistólica logo após a suspeita de sepse ou choque séptico"
                              type="text"
                              // placeholder="Pressão arterial sistólica"
                              value={formik.values.pas_diagnostico_sepse}
                              hasError={
                                (formik.errors.pas_diagnostico_sepse &&
                                  formik.touched.pas_diagnostico_sepse) ||
                                formik.submitCount > 0
                                  ? formik.errors.pas_diagnostico_sepse
                                  : ''
                              }
                              maxLength={3}
                              onChange={({ target }) => {
                                formik.setFieldValue(
                                  'pas_diagnostico_sepse',
                                  onlyNumbers(target.value)
                                );
                              }}
                            />
                          </Col>

                          <Col span={12} className="item">
                            <Input
                              name="pad_diagnostico_sepse"
                              label="Pressão arterial diastólica logo após a suspeita de sepse ou choque séptico"
                              type="text"
                              // placeholder="Pressão arterial diastólica"
                              value={formik.values.pad_diagnostico_sepse}
                              hasError={
                                (formik.errors.pad_diagnostico_sepse &&
                                  formik.touched.pad_diagnostico_sepse) ||
                                formik.submitCount > 0
                                  ? formik.errors.pad_diagnostico_sepse
                                  : ''
                              }
                              maxLength={3}
                              onChange={({ target }) => {
                                formik.setFieldValue(
                                  'pad_diagnostico_sepse',
                                  onlyNumbers(target.value)
                                );
                              }}
                            />
                          </Col>
                        </BoxInput>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </BoxForm>
              <hr />

              {/* Data e hora da 1ª coleta de lactato sérico */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <h4>
                        12{')'} Data e hora da 1ª coleta de lactato sérico
                      </h4>
                    </Row>
                    <Row>
                      {/* TODO Verificar se o errors[item.name] está correto */}
                      {dataHoraPrimeiraColeta.map(item => (
                        <Col span={12} className="item" key={item.id}>
                          <DadoFaltantes
                            title={item.label}
                            name={item.name}
                            formik={formik}
                            errors={formik.errors}
                            hasError={
                              (formik.errors[item.name] &&
                                formik.touched[item.name]) ||
                              formik.submitCount > 0
                                ? formik.errors[item.name]
                                : ''
                            }
                          />
                        </Col>
                      ))}
                    </Row>
                  </Col>
                </Row>
              </BoxForm>
              <hr />

              {/* Resultado da 1ª coleta de lactato sérico */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <Col span={12}>
                        <h4>
                          13{')'} Resultado da 1ª coleta de lactato sérico
                        </h4>
                      </Col>
                      {/* <Col span={12}>
                        {isLoaded && (
                          <ButtonReset
                            resetGroup={[{ name: 'lactato1_unidade_sepse' }]}
                            handleResetGroup={handleResetGroup}
                          />
                        )}
                      </Col> */}
                    </Row>
                    <Row>
                      <Col span={24}>
                        <BoxInput>
                          <Col span={12} className="item">
                            <Input
                              name="lactato1_resultado_sepse"
                              label="Resultado da 1ª coleta de lactato sérico"
                              type="text"
                              // placeholder="Resultado da 1ª coleta de lactato sérico"
                              value={formik.values.lactato1_resultado_sepse}
                              hasError={
                                (formik.errors.lactato1_resultado_sepse &&
                                  formik.touched.lactato1_resultado_sepse) ||
                                formik.submitCount > 0
                                  ? formik.errors.lactato1_resultado_sepse
                                  : ''
                              }
                              maxLength={5}
                              onChange={({ target }) => {
                                formik.setFieldValue(
                                  'lactato1_resultado_sepse',
                                  numbersAndComa(target.value)
                                );
                              }}
                            />
                          </Col>

                          <Col span={12} className="item">
                            <Row>
                              <Col span={18}>
                                <Label>
                                  Unidade de medida da 1ª coleta de lactato
                                  sérico
                                </Label>
                              </Col>
                              <Col span={6}>
                                {isLoaded && (
                                  <ButtonReset
                                    resetGroup={[
                                      { name: 'lactato1_unidade_sepse' },
                                    ]}
                                    handleResetGroup={handleResetGroup}
                                  />
                                )}
                              </Col>
                            </Row>
                            <div className="inlineRadios">
                              <BoxRadios
                                className="v-large"
                                active={
                                  formik.values.lactato1_unidade_sepse === '1'
                                }
                              >
                                <Radio
                                  name="lactato1_unidade_sepse"
                                  value="a"
                                  checked={
                                    formik.values.lactato1_unidade_sepse === '1'
                                  }
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `lactato1_unidade_sepse`,
                                      '1'
                                    )
                                  }
                                >
                                  mg/dL
                                </Radio>
                              </BoxRadios>
                              <BoxRadios
                                className="v-large"
                                active={
                                  formik.values.lactato1_unidade_sepse === '0'
                                }
                              >
                                <Radio
                                  name="lactato1_unidade_sepse"
                                  value="a"
                                  checked={
                                    formik.values.lactato1_unidade_sepse === '0'
                                  }
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `lactato1_unidade_sepse`,
                                      '0'
                                    )
                                  }
                                >
                                  mmo/L
                                </Radio>
                              </BoxRadios>
                            </div>
                            {formik.errors.lactato1_unidade_sepse &&
                            formik.submitCount > 0 ? (
                              <ErrorField
                                message={formik.errors.lactato1_unidade_sepse}
                              />
                            ) : null}
                          </Col>
                        </BoxInput>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </BoxForm>
              <hr />

              {/* Data e hora da 2ª coleta de lactato sérico */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <h4>
                        14{')'} Data e hora da 2ª coleta de lactato sérico
                      </h4>
                    </Row>
                    <Row>
                      {dataHoraSegundaColeta.map(item => (
                        <Col span={12} className="item" key={item.id}>
                          <DadoFaltantes
                            title={item.label}
                            name={item.name}
                            formik={formik}
                            errors={formik.errors}
                            hasError={
                              (formik.errors[item.name] &&
                                formik.touched[item.name]) ||
                              formik.submitCount > 0
                                ? formik.errors[item.name]
                                : ''
                            }
                          />
                        </Col>
                      ))}
                    </Row>
                  </Col>
                </Row>
              </BoxForm>
              <hr />

              {/* Resultado da 2ª coleta de lactato sérico */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <h4>15{')'} Resultado da 2ª coleta de lactato sérico</h4>

                    <Row>
                      <Col span={24}>
                        <BoxInput>
                          <Col span={12} className="item">
                            <Input
                              name="lactato2_resultado_sepse"
                              label="Resultado da 2ª coleta de lactato sérico"
                              type="text"
                              // placeholder="Resultado da 2ª coleta de lactato sérico"
                              value={formik.values.lactato2_resultado_sepse}
                              hasError={
                                (formik.errors.lactato2_resultado_sepse &&
                                  formik.touched.lactato2_resultado_sepse) ||
                                formik.submitCount > 0
                                  ? formik.errors.lactato2_resultado_sepse
                                  : ''
                              }
                              maxLength={5}
                              onChange={({ target }) => {
                                formik.setFieldValue(
                                  'lactato2_resultado_sepse',
                                  numbersAndComa(target.value)
                                );
                              }}
                            />
                          </Col>

                          <Col span={12} className="item">
                            <Row style={{ marginBottom: 3 }}>
                              <Col span={18}>
                                <Label>
                                  Unidade de medida da 2ª coleta de lactato
                                  sérico
                                </Label>
                              </Col>
                              <Col span={6}>
                                {isLoaded && (
                                  <ButtonReset
                                    resetGroup={[
                                      { name: 'lactato2_unidade_sepse' },
                                    ]}
                                    handleResetGroup={handleResetGroup}
                                  />
                                )}
                              </Col>
                            </Row>
                            <div className="inlineRadios">
                              <BoxRadios
                                className="v-large"
                                active={
                                  formik.values.lactato2_unidade_sepse === '1'
                                }
                              >
                                <Radio
                                  name="lactato2_unidade_sepse"
                                  value="a"
                                  checked={
                                    formik.values.lactato2_unidade_sepse === '1'
                                  }
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `lactato2_unidade_sepse`,
                                      '1'
                                    )
                                  }
                                >
                                  mg/dL
                                </Radio>
                              </BoxRadios>
                              <BoxRadios
                                className="v-large"
                                active={
                                  formik.values.lactato2_unidade_sepse === '0'
                                }
                              >
                                <Radio
                                  name="lactato2_unidade_sepse"
                                  value="a"
                                  checked={
                                    formik.values.lactato2_unidade_sepse === '0'
                                  }
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `lactato2_unidade_sepse`,
                                      '0'
                                    )
                                  }
                                >
                                  mmo/L
                                </Radio>
                              </BoxRadios>
                            </div>
                            {formik.errors.lactato2_unidade_sepse &&
                            formik.submitCount > 0 ? (
                              <ErrorField
                                message={formik.errors.lactato2_unidade_sepse}
                              />
                            ) : null}
                          </Col>
                        </BoxInput>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </BoxForm>
              <hr />

              {/* Data e hora da 1ª coleta de hemocultura */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <h4>16{')'} Data e hora da 1ª coleta de hemocultura</h4>
                    </Row>
                    <Row>
                      {dataHoraPrimeiraHemocultura.map(item => (
                        <Col span={12} className="item" key={item.id}>
                          <DadoFaltantes
                            title={item.label}
                            name={item.name}
                            formik={formik}
                            errors={formik.errors}
                            hasError={
                              (formik.errors[item.name] &&
                                formik.touched[item.name]) ||
                              formik.submitCount > 0
                                ? formik.errors[item.name]
                                : ''
                            }
                          />
                        </Col>
                      ))}
                    </Row>
                  </Col>
                </Row>
              </BoxForm>
              <hr />

              {/* Tratamento */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <Col span={12}>
                        <TitleSection>{'>'} Tratamento</TitleSection>
                      </Col>
                    </Row>

                    <Row>
                      <BoxInput>
                        <Col span={24} className="item">
                          <Row>
                            <Col span={18}>
                              <Label>
                                17{')'} Pacientes em uso de antimicrobianos no
                                momento da impressão diagnóstica de sepse ou
                                choque séptico
                              </Label>
                            </Col>
                            <Col span={6}>
                              {isLoaded && (
                                <ButtonReset
                                  resetGroup={[
                                    { name: 'antimicrobiano_previo_sepse' },
                                  ]}
                                  handleResetGroup={handleResetGroup}
                                />
                              )}
                            </Col>
                          </Row>

                          <Row>
                            <Col span={4}>
                              <BoxRadios
                                active={
                                  formik.values.antimicrobiano_previo_sepse ===
                                  '1'
                                }
                              >
                                <Radio
                                  name="antimicrobiano_previo_sepse"
                                  value="a"
                                  checked={
                                    formik.values
                                      .antimicrobiano_previo_sepse === '1'
                                  }
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `antimicrobiano_previo_sepse`,
                                      '1'
                                    )
                                  }
                                >
                                  Sim
                                </Radio>
                              </BoxRadios>
                            </Col>
                            <Col span={4}>
                              <BoxRadios
                                active={
                                  formik.values.antimicrobiano_previo_sepse ===
                                  '0'
                                }
                              >
                                <Radio
                                  name="antimicrobiano_previo_sepse"
                                  value="a"
                                  checked={
                                    formik.values
                                      .antimicrobiano_previo_sepse === '0'
                                  }
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `antimicrobiano_previo_sepse`,
                                      '0'
                                    )
                                  }
                                >
                                  Não
                                </Radio>
                              </BoxRadios>
                            </Col>
                          </Row>

                          {formik.errors.antimicrobiano_previo_sepse &&
                          formik.submitCount > 0 ? (
                            <ErrorField
                              message={
                                formik.errors.antimicrobiano_previo_sepse
                              }
                            />
                          ) : null}
                        </Col>
                      </BoxInput>
                    </Row>
                  </Col>
                </Row>
              </BoxForm>
              <hr />

              {/* Data e hora da 1ª dose de antimicrobiano após a suspeita de sepse ou choque séptico */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <h4>
                        18{')'} Data e hora da 1ª dose de antimicrobiano após a
                        suspeita de sepse ou choque séptico
                      </h4>
                    </Row>
                    <Row>
                      {dataHoraAntimicrobiano.map(item => (
                        <Col span={12} className="item" key={item.id}>
                          <DadoFaltantes
                            title={item.label}
                            name={item.name}
                            formik={formik}
                            errors={formik.errors}
                            hasError={
                              (formik.errors[item.name] &&
                                formik.touched[item.name]) ||
                              formik.submitCount > 0
                                ? formik.errors[item.name]
                                : ''
                            }
                          />
                        </Col>
                      ))}
                    </Row>
                  </Col>
                </Row>
              </BoxForm>
              <hr />

              {/* Paciente recebeu reposição volêmica adequada */}
              <BoxForm>
                <Row>
                  {reposicaoVolemicaAdequada.map(item => (
                    <Row>
                      <Row>
                        <Col span={12}>
                          <Label>{item.label}</Label>
                        </Col>
                        <Col span={12}>
                          {isLoaded && (
                            <ButtonReset
                              resetGroup={reposicaoVolemicaAdequada}
                              handleResetGroup={handleResetGroup}
                            />
                          )}
                        </Col>
                      </Row>
                      <BoxInput key={item.id}>
                        {item.opcoes.map(opcao => (
                          <Col span={12} key={opcao.id}>
                            <BoxRadios
                              active={formik.values[item.name] === opcao.value}
                              style={{ maxWidth: '100%' }}
                            >
                              <Radio
                                name={item.name}
                                value={formik.values[opcao.value]}
                                checked={
                                  formik.values[item.name] === opcao.value
                                }
                                onChange={() =>
                                  exibeRamificacao(item.name, opcao.value)
                                }
                              >
                                {opcao.descricao}
                              </Radio>
                            </BoxRadios>
                          </Col>
                        ))}
                      </BoxInput>

                      {formik.errors[item.name] && formik.submitCount > 0 ? (
                        <ErrorField message={formik.errors[item.name]} />
                      ) : null}
                    </Row>
                  ))}
                </Row>
              </BoxForm>

              {/* Data e hora da revisão da reposição volêmica */}
              {reposicaoVolemica && (
                <BoxForm>
                  <Row>
                    <Col span={24}>
                      <Row>
                        <h4>
                          20{')'} Data e hora do início da reposição volêmica
                        </h4>
                      </Row>
                      <Row>
                        {dataHoraReposicaoVolemica.map(item => (
                          <Col span={12} className="item" key={item.id}>
                            <DadoFaltantes
                              title={item.label}
                              name={item.name}
                              formik={formik}
                              errors={formik.errors}
                              hasError={
                                (formik.errors[item.name] &&
                                  formik.touched[item.name]) ||
                                formik.submitCount > 0
                                  ? formik.errors[item.name]
                                  : ''
                              }
                            />
                          </Col>
                        ))}
                      </Row>
                    </Col>
                  </Row>

                  <BoxForm>
                    <Row>
                      <Col span={24}>
                        <Row>
                          <h4>
                            21{')'} Pressão arterial imediatamente após a
                            infusão de solução para reposição volêmica
                          </h4>
                        </Row>
                        <Row>
                          <BoxInput>
                            <Input
                              name="pas_infusao_sepse"
                              label="Pressão arterial sistólica"
                              type="text"
                              // placeholder="Pressão arterial sistólica"
                              value={formik.values.pas_infusao_sepse}
                              hasError={
                                (formik.errors.pas_infusao_sepse &&
                                  formik.touched.pas_infusao_sepse) ||
                                formik.submitCount > 0
                                  ? formik.errors.pas_infusao_sepse
                                  : ''
                              }
                              maxLength={3}
                              onChange={({ target }) => {
                                formik.setFieldValue(
                                  'pas_infusao_sepse',
                                  onlyNumbers(target.value)
                                );
                              }}
                            />
                          </BoxInput>
                        </Row>
                        <Row>
                          <BoxInput>
                            <Input
                              name="pad_infusao_sepse"
                              label="Pressão arterial diastólica"
                              type="text"
                              // placeholder="Pressão arterial diastólica"
                              value={formik.values.pad_infusao_sepse}
                              hasError={
                                (formik.errors.pad_infusao_sepse &&
                                  formik.touched.pad_infusao_sepse) ||
                                formik.submitCount > 0
                                  ? formik.errors.pad_infusao_sepse
                                  : ''
                              }
                              maxLength={3}
                              onChange={({ target }) => {
                                formik.setFieldValue(
                                  'pad_infusao_sepse',
                                  onlyNumbers(target.value)
                                );
                              }}
                            />
                          </BoxInput>
                        </Row>
                      </Col>
                    </Row>
                  </BoxForm>
                  <hr />

                  {exibeInfusao && (
                    <BoxForm>
                      <Row>
                        <Col span={24}>
                          <Row>
                            <h4>
                              22{')'} Data e hora da medida da pressão arterial
                              após a reposição volêmica
                            </h4>
                          </Row>
                          <Row>
                            {medidaPressao.map(item => (
                              <Col span={12} className="item" key={item.id}>
                                <DadoFaltantes
                                  title={item.label}
                                  name={item.name}
                                  formik={formik}
                                  hasError={
                                    formik.errors[item.name] &&
                                    formik.submitCount > 0
                                      ? formik.errors[item.name]
                                      : ''
                                  }
                                />
                              </Col>
                            ))}
                          </Row>
                        </Col>
                      </Row>
                    </BoxForm>
                  )}

                  {reposicaoVolemica && (
                    <Row>
                      {vasopressores.map(item => (
                        <Row>
                          <Row>
                            <Col span={12}>
                              <Label>{item.label}</Label>
                            </Col>
                            <Col span={12}>
                              {isLoaded && (
                                <ButtonReset
                                  resetGroup={vasopressores}
                                  handleResetGroup={handleResetGroup}
                                />
                              )}
                            </Col>
                          </Row>
                          <BoxInput key={item.id}>
                            {item.opcoes.map(opcao => (
                              <Col span={12} key={opcao.id}>
                                <BoxRadios
                                  active={
                                    formik.values[item.name] === opcao.value
                                  }
                                  style={{ maxWidth: '100%' }}
                                >
                                  <Radio
                                    name={item.name}
                                    value={formik.values[opcao.value]}
                                    checked={
                                      formik.values[item.name] === opcao.value
                                    }
                                    onChange={() =>
                                      exibeRamificacao2(item.name, opcao.value)
                                    }
                                  >
                                    {opcao.descricao}
                                  </Radio>
                                </BoxRadios>
                              </Col>
                            ))}
                          </BoxInput>

                          {formik.errors[item.name] &&
                          formik.submitCount > 0 ? (
                            <ErrorField message={formik.errors[item.name]} />
                          ) : null}
                        </Row>
                      ))}
                    </Row>
                  )}
                </BoxForm>
              )}

              {vasop && (
                <BoxForm>
                  <Row>
                    <Col span={24}>
                      <Row>
                        <h4>
                          24{')'} Data e hora do início da infusão do
                          vasopressor
                        </h4>
                      </Row>
                      <Row>
                        {infusaoVasopressor.map(item => (
                          <Col span={12} className="item" key={item.id}>
                            <DadoFaltantes
                              title={item.label}
                              name={item.name}
                              formik={formik}
                              hasError={
                                formik.errors[item.name] &&
                                formik.submitCount > 0
                                  ? formik.errors[item.name]
                                  : ''
                              }
                            />
                          </Col>
                        ))}
                      </Row>
                    </Col>
                  </Row>
                </BoxForm>
              )}
              <hr />

              {/* Data e hora da revisão da terapia antimicrobiana */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <h4>
                        25{')'} Data e hora da revisão da terapia antimicrobiana
                      </h4>
                    </Row>
                    <Row>
                      {dataHoraRevisao.map(item => (
                        <Col span={12} className="item" key={item.id}>
                          <DadoFaltantes
                            title={item.label}
                            name={item.name}
                            formik={formik}
                            errors={formik.errors}
                            hasError={
                              (formik.errors[item.name] &&
                                formik.touched[item.name]) ||
                              formik.submitCount > 0
                                ? formik.errors[item.name]
                                : ''
                            }
                          />
                        </Col>
                      ))}
                    </Row>
                  </Col>
                </Row>
              </BoxForm>
              <hr />

              {/* Paciente em medidas de conforto ou cuidados paliativos */}
              <BoxForm>
                <Row>
                  <Col span={24}>
                    <Row>
                      <BoxInput>
                        <Col span={24} className="item">
                          <Row>
                            <Col span={18}>
                              <Label>
                                26{')'} Paciente em medidas de conforto ou
                                cuidados paliativos
                              </Label>
                            </Col>
                            <Col span={6}>
                              {isLoaded && (
                                <ButtonReset
                                  resetGroup={[{ name: 'paliativos_sepse' }]}
                                  handleResetGroup={handleResetGroup}
                                />
                              )}
                            </Col>
                          </Row>
                          <div className="inlineRadios">
                            <BoxRadios
                              className="v-large"
                              active={formik.values.paliativos_sepse === '1'}
                            >
                              <Radio
                                name="paliativos_sepse"
                                value="a"
                                checked={formik.values.paliativos_sepse === '1'}
                                onChange={() =>
                                  formik.setFieldValue(`paliativos_sepse`, '1')
                                }
                              >
                                Sim
                              </Radio>
                            </BoxRadios>
                            <BoxRadios
                              className="v-large"
                              active={formik.values.paliativos_sepse === '0'}
                            >
                              <Radio
                                name="paliativos_sepse"
                                value="a"
                                checked={formik.values.paliativos_sepse === '0'}
                                onChange={() =>
                                  formik.setFieldValue(`paliativos_sepse`, '0')
                                }
                              >
                                Não
                              </Radio>
                            </BoxRadios>
                          </div>
                          {formik.errors.paliativos_sepse &&
                          formik.submitCount > 0 ? (
                            <ErrorField
                              message={formik.errors.paliativos_sepse}
                            />
                          ) : null}
                        </Col>
                      </BoxInput>
                    </Row>
                  </Col>
                </Row>
              </BoxForm>
              <hr />

              {/* Covid Sepse */}
              <BoxForm>
                <Row>
                  <Row>
                    <Col span={12}>
                      <TitleSection>{'>'} Dados da saída</TitleSection>
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24} className="item">
                      {covidSepse.map(item => (
                        <Row>
                          <Row>
                            <Col span={12}>
                              <Label>{item.label}</Label>
                            </Col>
                            <Col span={12}>
                              {isLoaded && (
                                <ButtonReset
                                  resetGroup={[...covidSepse]}
                                  handleResetGroup={handleResetGroup}
                                />
                              )}
                            </Col>
                          </Row>
                          <BoxInput key={item.id}>
                            {item.opcoes.map(opcao => (
                              <Col span={12} key={opcao.id}>
                                <BoxRadios
                                  active={
                                    formik.values[item.name] === opcao.value
                                  }
                                  style={{ maxWidth: '100%' }}
                                >
                                  <Radio
                                    name={item.name}
                                    value={formik.values[opcao.value]}
                                    checked={
                                      formik.values[item.name] === opcao.value
                                    }
                                    onChange={() =>
                                      formik.setFieldValue(
                                        `${item.name}`,
                                        opcao.value
                                      )
                                    }
                                  >
                                    {opcao.descricao}
                                  </Radio>
                                </BoxRadios>
                              </Col>
                            ))}
                          </BoxInput>

                          {formik.errors[item.name] &&
                          formik.submitCount > 0 ? (
                            <ErrorField message={formik.errors[item.name]} />
                          ) : null}
                        </Row>
                      ))}
                    </Col>
                  </Row>
                </Row>
              </BoxForm>

              <BoxForm>
                <Row>
                  <Col span={24} className="item">
                    {motivoSaida.map(item => (
                      <RadioInputFlex
                        key={`${item.id}-${item.name}`}
                        item={item}
                        formik={formik}
                      />
                    ))}
                  </Col>

                  <Col span={24}>
                    <BoxForm>
                      <Row>
                        <Col span={24}>
                          <Row>
                            <h4>29{')'} Data e hora da saída</h4>
                          </Row>
                          <Row>
                            {dataHoraSaida.map(item => (
                              <Col span={12} className="item" key={item.id}>
                                <DadoFaltantes
                                  title={item.label}
                                  name={item.name}
                                  formik={formik}
                                  errors={formik.errors}
                                  hasError={
                                    (formik.errors[item.name] &&
                                      formik.touched[item.name]) ||
                                    formik.submitCount > 0
                                      ? formik.errors[item.name]
                                      : ''
                                  }
                                />
                              </Col>
                            ))}
                          </Row>
                        </Col>
                      </Row>
                    </BoxForm>
                  </Col>
                </Row>
              </BoxForm>
              <hr />

              <ButtonBox>
                <button type="button" onClick={callHandleSubmit}>
                  {loading ? 'Enviando dados...' : 'Salvar'}
                </button>
              </ButtonBox>
            </form>
          </Content>
        </BoxContent>
      )}
    </>
  );
};

FichadeColetaSepse.propTypes = {
  location: PropTypes.shape({
    state: PropTypes.shape({
      indicador: PropTypes.string,
      indicadorNome: PropTypes.string,
      periodo: PropTypes.string,
    }),
  }).isRequired,
};

export default FichadeColetaSepse;
